import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { AlertTriangle, ArrowLeft } from "lucide-react";

import { Button, Loading, Textarea } from "@/components/ui";
import { useToast } from "@/hooks";
import {
  BillingAPI,
  type SubscriptionSnapshot,
} from "@/services/billing";

function formatDate(iso: string | null | undefined): string | null {
  if (!iso) return null;
  // Date-only ISO strings (YYYY-MM-DD) parse as UTC midnight and may shift a
  // day backwards in local time (e.g. UTC-3). Render them as local dates.
  const dateOnly = /^\d{4}-\d{2}-\d{2}$/.test(iso);
  const d = dateOnly ? new Date(`${iso}T00:00:00`) : new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleDateString("pt-BR", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  });
}

export function SettingsBillingCancel() {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [subscription, setSubscription] = useState<SubscriptionSnapshot | null>(
    null
  );
  const [reason, setReason] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    BillingAPI.getSubscription()
      .then((sub) => {
        if (cancelled) return;
        setSubscription(sub);
      })
      .catch(() => {
        if (cancelled) return;
        setError(
          "Não foi possível carregar sua assinatura. Tente novamente em instantes."
        );
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const accessUntil = formatDate(
    subscription?.period_end ?? subscription?.end_date ?? null
  );

  async function handleConfirm() {
    if (submitting) return;
    setSubmitting(true);
    try {
      await BillingAPI.cancel(reason.trim() || undefined);
      toast({
        title: "Cancelamento confirmado",
        description: accessUntil
          ? `Você terá acesso ao plano até ${accessUntil}.`
          : "Você terá acesso até o fim do ciclo atual.",
      });
      navigate("/settings/usage");
    } catch {
      setSubmitting(false);
      toast({
        title: "Erro ao cancelar",
        description:
          "Não conseguimos processar o cancelamento agora. Tente novamente em instantes.",
        variant: "destructive",
      });
    }
  }

  return (
    <section className="container mx-auto max-w-2xl px-4 py-10">
      <header className="mb-6">
        <h1 className="text-2xl font-bold text-foreground md:text-3xl">
          Cancelar assinatura
        </h1>
        <p className="mt-1 text-sm text-muted-foreground">
          Antes de confirmar, leia com atenção o que acontece após o
          cancelamento.
        </p>
      </header>

      {loading && (
        <div className="flex items-center justify-center py-24">
          <Loading />
        </div>
      )}

      {error && !loading && (
        <div className="mb-6 rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-700">
          {error}
        </div>
      )}

      {!loading && !error && subscription === null && (
        <div className="rounded-lg border border-amber-200 bg-amber-50 p-4 text-sm text-amber-800">
          Você ainda não tem uma assinatura ativa.
        </div>
      )}

      {!loading && !error && subscription && (
        <>
          <div
            className="mb-6 flex items-start gap-3 rounded-lg border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900"
            data-testid="retention-disclosure"
          >
            <AlertTriangle className="mt-0.5 h-5 w-5 shrink-0" />
            <div className="space-y-2">
              <p>
                {accessUntil ? (
                  <>
                    Você terá acesso até{" "}
                    <span className="font-semibold">{accessUntil}</span>.
                  </>
                ) : (
                  <>Você terá acesso até o fim do ciclo atual.</>
                )}{" "}
                Após isso, seus dados serão retidos por{" "}
                <span className="font-semibold">90 dias</span> antes de exclusão
                definitiva. Durante esse período você pode reativar a
                assinatura sem perder nada.
              </p>
              <p className="text-xs">
                Após 90 dias, seus dados são apagados permanentemente conforme a
                política de retenção da CliniSharp e da LGPD.
              </p>
            </div>
          </div>

          <label className="mb-6 block text-sm">
            <span className="text-foreground">
              O que poderíamos melhorar? (opcional)
            </span>
            <Textarea
              data-testid="cancel-reason"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="Conte um pouco do que motivou seu cancelamento. Sua resposta nos ajuda a melhorar o produto."
              rows={4}
              maxLength={2000}
              className="mt-2"
            />
          </label>

          <div className="flex flex-col-reverse gap-3 sm:flex-row sm:justify-end">
            <Button
              type="button"
              variant="outline"
              onClick={() => navigate(-1)}
              disabled={submitting}
              data-testid="cancel-back"
            >
              <ArrowLeft className="mr-2 h-4 w-4" />
              Voltar
            </Button>
            <Button
              type="button"
              variant="destructive"
              onClick={handleConfirm}
              disabled={submitting}
              data-testid="cancel-confirm"
            >
              {submitting ? "Processando..." : "Confirmar cancelamento"}
            </Button>
          </div>
        </>
      )}
    </section>
  );
}
